import React, {
    useEffect,
    useRef,
    useState
} from "react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";

import {
    getLabourRateList,
    updateLabourRate
} from "../../../services/labourRateService";
import PageHeader from "../../../utils/PageHeader";

import { api } from "../../../../src/component/constent";

export const currentMonth = () => {
    return api.get(`/entry/current-month`);
};

// ============================================================
// COMPONENT
// ============================================================

const LabourRateUpdate = () => {


    // ========================================================
    // STATE
    // ========================================================

    const [rows, setRows] = useState([]);


const [month, setMonth] = useState("");
    const [year, setYear] = useState("");

    const [form, setForm] = useState({
        shop: "",
        hsk: "",
        sk: "",
        ssk: "",
        usk: "",
        stoc: "",
        lboc: ""
    });

    const [loading, setLoading] = useState(false);


    // ========================================================
    // REFS
    // ========================================================

    const shopRef = useRef(null);
    const hskRef = useRef(null);
    const skRef = useRef(null);
    const sskRef = useRef(null);
    const uskRef = useRef(null);
    const stocRef = useRef(null);
    const lbocRef = useRef(null);
    const updateRef = useRef(null);


    // ========================================================
    // LOAD DATA
    // ========================================================


 const loadInitialData = async () => {

        try {

            setLoading(true);

            const [
                monthResponse
            ] = await Promise.all([
                   currentMonth()
            ]);

           
            const currentMonthData =
                monthResponse.data.data;

            const currentMonthValue =
                currentMonthData.month;

            const currentYearValue =
                currentMonthData.year;

            setMonth(currentMonthValue);
            setYear(currentYearValue);

          
          

        } catch (error) {

            console.error(
                "Initial load error:",
                error
            );

            alert(
                error.response?.data?.message ||
                "Unable to load IOH data"
            );

        } finally {

            setLoading(false);

        }

    };






    const loadLabourRates = async () => {

        try {

            setLoading(true);

            const res =
                await getLabourRateList();

            if (res.data.success) {

                setRows(
                    res.data.data || []
                );

            }

        } catch (error) {

            console.error(
                "Labour rate load error:",
                error
            );

            alert(
                error?.response?.data?.message ||
                "Unable to load labour rates"
            );

        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // INITIAL LOAD
    // ========================================================

    useEffect(() => {

        loadLabourRates();
loadInitialData();
        setTimeout(() => {
            shopRef.current?.focus();
        }, 300);

    }, []);


    // ========================================================
    // FORM CHANGE
    // ========================================================

    const handleChange = (field, value) => {

        setForm(prev => ({
            ...prev,
            [field]: value
        }));

    };


    // ========================================================
    // SELECT SHOP
    // ========================================================

    const selectShop = (row) => {

        setForm({
            shop: row.ShopNumber,
            hsk: row.Rate1 ?? "",
            sk: row.Rate2 ?? "",
            ssk: row.Rate3 ?? "",
            usk: row.Rate4 ?? "",
            stoc: row.Rate5 ?? "",
            lboc: row.Rate6 ?? ""
        });

        setTimeout(() => {
            hskRef.current?.focus();
        }, 100);

    };


    // ========================================================
    // ENTER KEY NAVIGATION
    // ========================================================

    const handleEnter = (
        e,
        nextRef
    ) => {

        if (e.key === "Enter") {

            e.preventDefault();

            nextRef?.current?.focus();

        }

    };


    // ========================================================
    // VALIDATION
    // ========================================================

    const validateForm = () => {

        if (
            form.shop === "" ||
            form.shop === null
        ) {

            alert(
                "Please select Shop"
            );

            shopRef.current?.focus();

            return false;

        }


        const fields = [
            {
                name: "HSK",
                value: form.hsk,
                ref: hskRef
            },
            {
                name: "Skilled",
                value: form.sk,
                ref: skRef
            },
            {
                name: "Semi Skill",
                value: form.ssk,
                ref: sskRef
            },
            {
                name: "Un Skilled",
                value: form.usk,
                ref: uskRef
            },
            {
                name: "Store O/C",
                value: form.stoc,
                ref: stocRef
            },
            {
                name: "Labour O/C",
                value: form.lboc,
                ref: lbocRef
            }
        ];


        for (const field of fields) {

            if (
                field.value === "" ||
                field.value === null ||
                field.value === undefined ||
                !Number.isFinite(
                    Number(field.value)
                )
            ) {

                alert(
                    `Please enter ${field.name} rate`
                );

                field.ref.current?.focus();

                return false;

            }


            if (
                Number(field.value) < 0
            ) {

                alert(
                    `${field.name} rate cannot be negative`
                );

                field.ref.current?.focus();

                return false;

            }

        }


        return true;

    };


    // ========================================================
    // UPDATE
    // ========================================================

    const handleUpdate = async () => {

        if (!validateForm()) {
            return;
        }


        try {

            setLoading(true);

            const res =
                await updateLabourRate({

                    shop: form.shop,

                    hsk: Number(form.hsk),

                    sk: Number(form.sk),

                    ssk: Number(form.ssk),

                    usk: Number(form.usk),

                    stoc: Number(form.stoc),

                    lboc: Number(form.lboc),

                    month: month,
                    year: year

                });


            if (res.data.success) {

                alert(
                    res.data.message ||
                    "Labour rate updated successfully"
                );


                // Reload table
                await loadLabourRates();


                // Keep updated shop selected
                if (res.data.data) {

                    selectShop(
                        res.data.data
                    );

                }

            }

        } catch (error) {

            console.error(
                "Labour rate update error:",
                error
            );

            alert(
                error?.response?.data?.message ||
                "Unable to update labour rate"
            );

        } finally {

            setLoading(false);

        }

    };


    // ========================================================
    // EXCEL EXPORT
    // ========================================================

    const exportToExcel = () => {

        if (!rows.length) {

            alert(
                "No data available for export"
            );

            return;

        }


        const headers = [
            "Shop",
            "High Skill",
            "Skilled",
            "Semi Skilled",
            "Un Skilled",
            "Store O/C",
            "Labour O/C",
            "Updated By",
            "Data Year",
            "Rate Entry Date"
        ];


        const data = rows.map(row => [

            row.ShopNumber,

            row.Rate1,

            row.Rate2,

            row.Rate3,

            row.Rate4,

            row.Rate5,

            row.Rate6,

            row.UpdatedBy,

            row.DATAYEAR,

            row.RateEntryDate

        ]);


        const csv = [

            headers,

            ...data

        ]
            .map(row =>
                row
                    .map(value =>
                        `"${String(
                            value ?? ""
                        ).replaceAll(
                            '"',
                            '""'
                        )}"`
                    )
                    .join(",")
            )
            .join("\n");


        const blob =
            new Blob(
                [csv],
                {
                    type:
                        "text/csv;charset=utf-8;"
                }
            );


        const url =
            URL.createObjectURL(blob);


        const link =
            document.createElement("a");

        link.href = url;

        link.download =
            "LabourRate.xlsx.csv";

        link.click();

        URL.revokeObjectURL(url);

    };


    // ========================================================
    // RENDER
    // ========================================================

    return (

        <Box
            sx={{
                width: "100%",
                minHeight: "100%",
                p: {
                    xs: 1,
                    sm: 2
                },
                backgroundColor: "#f5f5f5"
            }}
        >

            {/* =================================================
                TITLE
            ================================================= */}

           <PageHeader
        title=" Labour Rate Update"
        icon={AccessTimeIcon}
       //rowsCount={rows.length}
       statusChip={{
          label: loading
            ? "Loading..."
            : rows.length > 0
              ? rows.length + " Records Found"
              : "No Data",
          color: loading ? "warning" : rows.length > 0 ? "primary" : "default",
        }}
      />




      


            {/* =================================================
                ENTRY PANEL
            ================================================= */}

            <Paper
                elevation={2}
                sx={{
                    p: {
                        xs: 1,
                        sm: 2
                    },
                    mb: 1.5,
                    background:
                        "linear-gradient(90deg,#eaffff,#cfffff)"
                }}
            >

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr 1fr",
                            sm:
                                "110px repeat(6, minmax(100px,1fr)) auto"
                        },
                        gap: 1,
                        alignItems: "end"
                    }}
                >

                    {/* SHOP */}

                    <Box>

                        <Typography
                            sx={{
                                color: "#333",
                                fontSize: 13,
                                mb: .4
                            }}
                        >
                            Shop
                        </Typography>

                        <TextField
                            inputRef={shopRef}
                            size="small"
                            value={form.shop}
                            onChange={(e) =>
                                handleChange(
                                    "shop",
                                    e.target.value
                                )
                            }
                            onKeyDown={(e) =>
                                handleEnter(
                                    e,
                                    hskRef
                                )
                            }
                            fullWidth
                            inputProps={{
                                inputMode: "numeric"
                            }}
                        />

                    </Box>


                    {/* HSK */}

                    <RateField
                        label="High Skill"
                        value={form.hsk}
                        onChange={(value) =>
                            handleChange(
                                "hsk",
                                value
                            )
                        }
                        inputRef={hskRef}
                        nextRef={skRef}
                    />


                    {/* SK */}

                    <RateField
                        label="Skilled"
                        value={form.sk}
                        onChange={(value) =>
                            handleChange(
                                "sk",
                                value
                            )
                        }
                        inputRef={skRef}
                        nextRef={sskRef}
                    />


                    {/* SSK */}

                    <RateField
                        label="Semi Skill"
                        value={form.ssk}
                        onChange={(value) =>
                            handleChange(
                                "ssk",
                                value
                            )
                        }
                        inputRef={sskRef}
                        nextRef={uskRef}
                    />


                    {/* USK */}

                    <RateField
                        label="Un Skilled"
                        value={form.usk}
                        onChange={(value) =>
                            handleChange(
                                "usk",
                                value
                            )
                        }
                        inputRef={uskRef}
                        nextRef={stocRef}
                    />


                    {/* STOC */}

                    <RateField
                        label="Store O/C"
                        value={form.stoc}
                        onChange={(value) =>
                            handleChange(
                                "stoc",
                                value
                            )
                        }
                        inputRef={stocRef}
                        nextRef={lbocRef}
                    />


                    {/* LBOC */}

                    <RateField
                        label="Labour O/C"
                        value={form.lboc}
                        onChange={(value) =>
                            handleChange(
                                "lboc",
                                value
                            )
                        }
                        inputRef={lbocRef}
                        nextRef={updateRef}
                    />


                    {/* UPDATE */}

                    <Button
                        ref={updateRef}
                        variant="contained"
                        onClick={handleUpdate}
                        disabled={loading}
                        onKeyDown={(e) =>
                            handleEnter(
                                e,
                                shopRef
                            )
                        }
                        sx={{
                            height: 42,
                            minWidth: 100,
                            backgroundColor:
                                "#9c0b7b",
                            "&:hover": {
                                backgroundColor:
                                    "#780860"
                            }
                        }}
                    >
                        {loading
                            ? "Updating..."
                            : "Update"}
                    </Button>

                </Box>

            </Paper>


            {/* =================================================
                TABLE
            ================================================= */}

            <Paper
                elevation={2}
                sx={{
                    p: 1,
                    borderRadius: 0
                }}
            >

                <TableContainer
                    sx={{
                        maxHeight: {
                            xs: "55vh",
                            sm: "65vh"
                        },
                        overflowX: "auto"
                    }}
                >

                    <Table
                        stickyHeader
                        size="small"
                    >

                        <TableHead>

                            <TableRow>

                                <TableCell>
                                    shop
                                </TableCell>

                                <TableCell>
                                    High Skill
                                </TableCell>

                                <TableCell>
                                    Skilled
                                </TableCell>

                                <TableCell>
                                    Semi Skilled
                                </TableCell>

                                <TableCell>
                                    Un Skilled
                                </TableCell>

                                <TableCell>
                                    Store O/C
                                </TableCell>

                                <TableCell>
                                    Labour O/C
                                </TableCell>

                                <TableCell>
                                    UpdatedBy
                                </TableCell>

                                {/* <TableCell>
                                    Year
                                </TableCell> */}

                                <TableCell>
                                    Rate Entry Date
                                </TableCell>

                            </TableRow>

                        </TableHead>


                        <TableBody>

                            {rows.map((row) => (

                                <TableRow
                                    key={
                                        row.ShopNumber
                                    }
                                    hover
                                    onDoubleClick={() =>
                                        selectShop(row)
                                    }
                                    sx={{
                                        cursor:
                                            "pointer"
                                    }}
                                >

                                    <TableCell>
                                        {row.ShopNumber}
                                    </TableCell>

                                    <TableCell>
                                        {row.Rate1}
                                    </TableCell>

                                    <TableCell>
                                        {row.Rate2}
                                    </TableCell>

                                    <TableCell>
                                        {row.Rate3}
                                    </TableCell>

                                    <TableCell>
                                        {row.Rate4}
                                    </TableCell>

                                    <TableCell>
                                        {row.Rate5}
                                    </TableCell>

                                    <TableCell>
                                        {row.Rate6}
                                    </TableCell>

                                    <TableCell>
                                        {row.UpdatedBy}
                                    </TableCell>

                                    {/* <TableCell>
                                        {row.DATAYEAR}
                                    </TableCell> */}

                                    <TableCell>
                                        {row.RateEntryDate
                                            ? new Date(
                                                row.RateEntryDate
                                            ).toLocaleString()
                                            : ""}
                                    </TableCell>

                                </TableRow>

                            ))}


                            {!rows.length && !loading && (

                                <TableRow>

                                    <TableCell
                                        colSpan={10}
                                        align="center"
                                    >
                                        No labour rate data found
                                    </TableCell>

                                </TableRow>

                            )}

                        </TableBody>

                    </Table>

                </TableContainer>

            </Paper>


            {/* =================================================
                EXPORT
            ================================================= */}

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 1
                }}
            >

                <Button
                    variant="contained"
                    onClick={exportToExcel}
                    sx={{
                        backgroundColor:
                            "#ff8c00",
                        "&:hover": {
                            backgroundColor:
                                "#e07800"
                        }
                    }}
                >
                    Export to Excel
                </Button>

            </Box>

        </Box>

    );

};


// ============================================================
// RATE FIELD
// ============================================================

const RateField = ({
    label,
    value,
    onChange,
    inputRef,
    nextRef
}) => {

    return (

        <Box>

            <Typography
                sx={{
                    color: "#e00000",
                    fontSize: 13,
                    mb: .4,
                    whiteSpace: "nowrap"
                }}
            >
                {label}
            </Typography>

            <TextField
                inputRef={inputRef}
                size="small"
                fullWidth
                value={value}
                onChange={(e) =>
                    onChange(
                        e.target.value
                    )
                }
                onKeyDown={(e) => {

                    if (e.key === "Enter") {

                        e.preventDefault();

                        nextRef?.current?.focus();

                    }

                }}
                inputProps={{
                    inputMode: "decimal"
                }}
            />

        </Box>

    );

};


export default LabourRateUpdate;